import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Container, Nav, NavDropdown, Modal, Button, Alert, Badge } from 'react-bootstrap';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';

// Helper function to get initials
const getInitials = (name) => {
  if (!name) return '?';
  // Try splitting by space for first/last name
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  // Try splitting by @ for email
  const emailParts = name.split('@');
  if (emailParts[0]) {
    return emailParts[0].substring(0, 2).toUpperCase();
  }
  // Fallback to first 2 chars
  return name.substring(0, 2).toUpperCase();
};

const HeaderWrapper = styled.header`
  background-color: ${props => props.theme?.secondary_color || '#f8f9fa'}; // Lighter default
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;

  .navbar-light .navbar-nav .nav-link {
    color: ${props => props.theme?.primary_color || '#343a40'}; // Darker text
    &:hover {
      color: ${props => props.theme?.primary_color ? `${props.theme.primary_color}99` : '#0056b3'};
    }
  }
  /* Style for the new dropdown toggle */
  .user-dropdown-toggle.dropdown-toggle::after {
      display: none; /* Hide default caret */
  }
  .user-dropdown-toggle {
      background-color: transparent !important; 
      border: none !important;
      color: ${props => props.theme?.primary_color || '#343a40'} !important; 
      padding: 0.5rem 0.5rem !important; /* Adjust padding for icon */
      display: inline-flex;
      align-items: center;
  }
  .user-dropdown-toggle:hover, .user-dropdown-toggle:focus {
       color: ${props => props.theme?.primary_color ? `${props.theme.primary_color}99` : '#0056b3'} !important;
       background-color: transparent !important;
       box-shadow: none !important;
  }
`;

const Logo = styled.img`
  height: 40px;
  max-width: 200px;
  object-fit: contain;
`;

// Style the link like a button
const ReturnToDefinedLink = styled.a`
  display: inline-flex; // Use inline-flex to fit content
  align-items: center;
  padding: 0.375rem 0.75rem; // Standard Bootstrap button padding
  margin-left: 15px;
  font-size: 0.9rem; // Adjust font size as needed
  font-weight: 500; // Slightly bolder text
  line-height: 1.5;
  color: #fff; // White text/icon
  background-color: #0d6efd; // Bootstrap primary blue
  border: 1px solid #0d6efd;
  border-radius: 0.25rem; // Standard Bootstrap border-radius
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;

  &:hover {
    color: #fff;
    background-color: #0b5ed7; // Darker blue on hover
    border-color: #0a58ca;
    text-decoration: none;
  }

  svg {
    height: 1em; // Match font size
    width: auto; 
    margin-right: 0.4rem; // Space between icon and text
    /* Set stroke directly or use fill if the SVG structure changes */
    path {
        stroke: currentColor; /* Inherit color (white) */
    }
  }
`;

// Styled component for the initials circle
const InitialsCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;  /* Adjust size */
  height: 32px; /* Adjust size */
  border-radius: 50%;
  background-color: #6c757d; /* Bootstrap secondary color */
  color: white;
  font-size: 0.8rem; /* Adjust font size */
  font-weight: 600;
  margin-right: 0.5rem;
  flex-shrink: 0; /* Prevent shrinking */
`;

const UserNameDisplay = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px; /* Limit width */
    display: inline-block; /* Necessary for max-width */
    vertical-align: middle; /* Align with initials circle */
`;

// Styled component for the Preview Mode Banner
const PreviewModeBanner = styled(Alert)`
  background-color: ${props => props.theme?.warning_color || '#ffc107'}; // Use a theme color or default yellow
  color: ${props => props.theme?.warning_text_color || '#212529'}; // Dark text for contrast
  padding: 0.5rem 1rem;
  margin-bottom: 0; // Remove default margin if Alert has one
  border-radius: 0; // Make it a strip
  text-align: center;
  font-size: 0.9rem;
  width: 100%;
  z-index: 1001; // Ensure it's above other header content if needed

  .switch-back-link {
    font-weight: bold;
    color: ${props => props.theme?.warning_link_color || '#0056b3'};
    text-decoration: underline;
    cursor: pointer;
    margin-left: 10px;

    &:hover {
      color: ${props => props.theme?.warning_link_hover_color || '#003d80'};
    }
  }
`;

const Header = ({ logo, theme }) => {
  const {
    currentUser,
    isLoggedIn,
    logout,
    setLoginRedirectPath,
    isInPreviewMode,  
    exitPreviewMode,  
    enterPreviewMode, 
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // --- State --- 
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);

  // --- Effects --- 

  // --- Handlers ---
  const handleShowUserInfoModal = () => setShowUserInfoModal(true);
  const handleCloseUserInfoModal = () => setShowUserInfoModal(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLoginClick = () => {
    setLoginRedirectPath(location.pathname + location.search);
    navigate('/login');
  };

  // --- Render Logic ---
  const displayIdentifier = currentUser?.email || currentUser?.username || '';
  
  // Determine effective role for UI display, especially in preview mode
  const effectiveRole = isInPreviewMode ? 'student' : currentUser?.role;
  const userRoleForDisplay = effectiveRole ? (effectiveRole.charAt(0).toUpperCase() + effectiveRole.slice(1)) : 'Guest';

  const isLocalAuthentication = currentUser ? (currentUser.is_local ?? true) : true;
  const userInitials = getInitials(displayIdentifier);

  const returnUrl = currentUser?.role === 'student'
      ? "https://go.definedlearning.com" 
      : "https://go.definedlearning.com/products/defined-careers";

  return (
    <>
      <HeaderWrapper theme={theme}>
        {/* Student Preview Mode Banner */}
        {isLoggedIn && isInPreviewMode && (
          <PreviewModeBanner variant="warning" theme={theme}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            You are currently in <strong>Student Preview Mode</strong>.
            <span 
              className="switch-back-link"
              onClick={exitPreviewMode} 
              onKeyDown={(e) => e.key === 'Enter' && exitPreviewMode()} // Accessibility for keyboard
              role="button" 
              tabIndex={0} // Make it focusable
              aria-label="Switch back to teacher view"
            >
              Exit Preview
            </span>
          </PreviewModeBanner>
        )}
        <Container>
          <Navbar expand="lg" className="navbar-light">
            <Link 
              to={isLoggedIn ? (effectiveRole === 'student' ? '/' : (currentUser?.isAdmin ? '/' : '/')) : "/"} 
              className="navbar-brand"
            >
              <Logo src={logo || '/images/definedatwork.svg'} alt="Portal Logo" />
            </Link>
            <Navbar.Toggle aria-controls="navbarNav" />
            <Navbar.Collapse id="navbarNav">
              <Nav className="ms-auto align-items-center">
                {/* Common Links */} 
                <Nav.Link as={Link} to="/job-board">
                  <i className="fas fa-briefcase me-1"></i>
                  Jobs
                </Nav.Link>
                <Nav.Link as={Link} to="/companies">
                  <i className="fas fa-building me-1"></i>
                  Companies
                </Nav.Link>
                <Nav.Link as={Link} to="/career-tools">
                  <i className="fas fa-toolbox me-1"></i>
                  Career Tools
                </Nav.Link>

                {/* My Activities link for students and preview mode */}
                {(isLoggedIn && (effectiveRole === 'student' || isInPreviewMode)) && (
                  <Nav.Link
                    as={Link}
                    to="/dashboard"
                    aria-label="My Activities"
                    title="My Activities"
                  >
                    <i className="fas fa-list-check me-1" aria-hidden="true"></i>
                    My Activities
                  </Nav.Link>
                )}

                {/* Conditional Login/User Info */} 
                {isLoggedIn && !isLocalAuthentication && (
                  <ReturnToDefinedLink 
                    href={returnUrl}
                    target="_self" 
                    rel="noopener noreferrer"
                    aria-label="Return To Defined"
                    title="Return To Defined"
                  >
                     {/* New SVG first */}
                     <svg 
                       width="1em" /* Control size via parent font-size */
                       height="1em"
                       viewBox="0 0 16 16" /* Updated viewBox */
                       fill="none" /* Keep fill=none on svg if path has fill */
                       xmlns="http://www.w3.org/2000/svg"
                       aria-hidden="true" /* Hide decorative SVG from screen readers */
                       className="return-icon-svg" /* Add class for styling */
                      >
                        {/* Updated path with fill="currentColor" */}
                        <path 
                            fill="currentColor" 
                            fillRule="evenodd" /* Use camelCase for React props */
                            d="M4.297105,3.29289 L0.59,7 L4.297105,10.7071 C4.687635,11.0976 5.320795,11.0976 5.711315,10.7071 C6.101845,10.3166 6.101845,9.68342 5.711315,9.29289 L4.418425,8 L11.504215,8 C12.332615,8 13.004215,8.67157 13.004215,9.5 C13.004215,10.3284 12.332615,11 11.504215,11 L10.004215,11 C9.451935,11 9.004215,11.4477 9.004215,12 C9.004215,12.5523 9.451935,13 10.004215,13 L11.504215,13 C13.437215,13 15.004215,11.433 15.004215,9.5 C15.004215,7.567 13.437215,6 11.504215,6 L4.418425,6 L5.711315,4.70711 C6.101845,4.31658 6.101845,3.68342 5.711315,3.29289 C5.320795,2.90237 4.687635,2.90237 4.297105,3.29289 Z"
                        />
                      </svg>
                     {/* Text after icon */}
                     Return to Defined
                  </ReturnToDefinedLink>
                )}

                {isLoggedIn ? (
                  <NavDropdown 
                    title={<i className="fas fa-user-circle fa-lg"></i>}
                    id="user-dropdown" 
                    align="end" 
                    className="user-dropdown-toggle"
                  >
                    <NavDropdown.Header>
                      {currentUser?.firstName ? `${currentUser.firstName} ${currentUser.lastName}` : displayIdentifier}
                      {isInPreviewMode && <Badge bg="warning" text="dark" className="ms-2">Student Preview</Badge>}
                    </NavDropdown.Header>
                    
                    {/* Dashboard link - behavior changes in preview mode */}
                    {isLoggedIn && (
                      <NavDropdown.Item as={Link} to="/dashboard">
                        <i className={`fas ${isInPreviewMode || effectiveRole === 'student' ? 'fa-tachometer-alt' : (currentUser?.isAdmin ? 'fa-user-shield' : 'fa-chalkboard-teacher')} me-2`}></i>
                        {isInPreviewMode ? 'Student Activities' : (effectiveRole === 'student' ? 'My Activities' : (currentUser?.isAdmin ? 'Admin Dashboard' : 'Educator Dashboard'))}
                      </NavDropdown.Item>
                    )}

                    {/* Option to Enter Student Preview Mode - only for educators not in preview */}
                    {isLoggedIn && currentUser?.role === 'educator' && !isInPreviewMode && (
                      <NavDropdown.Item onClick={enterPreviewMode}>
                        <i className="fas fa-user-graduate me-2"></i>Preview as Student
                      </NavDropdown.Item>
                    )}

                    {/* View Role - shows actual role unless in preview, then clarifies */}
                    <NavDropdown.Item onClick={handleShowUserInfoModal}>
                       <i className="fas fa-info-circle me-2"></i>
                       {isInPreviewMode ? 'Viewing as Student' : 'View My Role'}
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.Item onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2"></i>Logout
                    </NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Nav.Link onClick={handleLoginClick} style={{ cursor: 'pointer' }}>
                    <i className="fas fa-sign-in-alt me-1"></i>
                    Login
                  </Nav.Link>
                )}
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        </Container>
      </HeaderWrapper>

      {/* --- User Info Modal --- */} 
      <Modal show={showUserInfoModal} onHide={handleCloseUserInfoModal} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title>User Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>Identifier:</strong> {displayIdentifier}</p>
          <p><strong>Your Actual Role:</strong> {userRoleForDisplay}</p>
          {isInPreviewMode && <p className="text-warning"><strong>Currently in:</strong> Student Preview Mode</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt me-1"></i> Logout
          </Button>
          <Button variant="secondary" onClick={handleCloseUserInfoModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Header;
